import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import BadgeImpl from 'components/v2/Badge';
import EditButtonImpl from 'components/v2/buttons/EditButton';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import { COMPLETE_STOP, CONFIRM_APPT, NEXT_ACTIONS_STATUS, NEXT_ACTION_COLOR, } from 'constants/next-actions';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { getTimezone } from 'helpers/stops';
const Badge = BadgeImpl;
const EditButton = EditButtonImpl;
const ACTIONS_WITHOUT_EDIT = [CONFIRM_APPT];
function getEquipmentIds(action) {
    var _a, _b, _c, _d;
    if ((_b = (_a = action.shipment) === null || _a === void 0 ? void 0 : _a.equipments) === null || _b === void 0 ? void 0 : _b.length)
        return action.shipment.equipments;
    if ((_d = (_c = action.nextStop) === null || _c === void 0 ? void 0 : _c.container) === null || _d === void 0 ? void 0 : _d.equipment_id) {
        return [action.nextStop.container.equipment_id];
    }
    return [];
}
function StatusCell({ row, onClick, }) {
    const { t } = useTranslation();
    const status = NEXT_ACTIONS_STATUS[row.action];
    return (React.createElement(Badge, { className: "w-40", color: NEXT_ACTION_COLOR[status], onClick: () => onClick(row) }, t(status)));
}
function ShipmentLinkCell({ row }) {
    return (React.createElement("a", { className: "underline", href: `/shipments/${row.shipment._id}`, target: "_blank", rel: "noreferrer noopener" },
        React.createElement("div", { className: "flex items-center space-x-1" },
            React.createElement("span", null, row.shipment.shipment_id),
            React.createElement(ExternalLinkIcon, { className: "w-4 h-4" }))));
}
function ContainerCell({ row }) {
    const ids = getEquipmentIds(row);
    if (!ids.length)
        return React.createElement("span", { className: "text-muted-foreground" }, '—');
    return (React.createElement("div", { className: "text-sm" }, ids.map((id) => (React.createElement("div", { key: id }, id)))));
}
function ShipperCell({ row }) {
    const shipper = row.shipment.shipper;
    if (!(shipper === null || shipper === void 0 ? void 0 : shipper._id))
        return React.createElement("span", { className: "text-muted-foreground" }, '—');
    return (React.createElement("a", { className: "underline flex items-center space-x-1", href: `/users/${shipper._id}`, target: "_blank", rel: "noreferrer noopener" },
        React.createElement("span", null, unescapeSpecialChars(shipper.company_name || '')),
        React.createElement(ExternalLinkIcon, { className: "w-4 h-4" })));
}
function CarrierCell({ row }) {
    const carrier = row.shipment.carrier;
    if (!(carrier === null || carrier === void 0 ? void 0 : carrier._id))
        return React.createElement("span", { className: "text-muted-foreground" }, '—');
    return (React.createElement("a", { className: "underline flex items-center space-x-1", href: `/users/${carrier._id}`, target: "_blank", rel: "noreferrer noopener" },
        React.createElement("span", null, unescapeSpecialChars(carrier.name || '')),
        React.createElement(ExternalLinkIcon, { className: "w-4 h-4" })));
}
function NextActionCell({ row }) {
    var _a;
    const { t } = useTranslation();
    const text = ((_a = row.nextStop) === null || _a === void 0 ? void 0 : _a.action) || row.action;
    return (React.createElement("div", { className: "text-sm" },
        React.createElement("p", { className: classnames({
                'font-medium': !!row.subActionMessage,
            }) }, t(text)),
        row.subActionMessage && (React.createElement("p", { className: "italic" }, t(row.subActionMessage)))));
}
function ApptScheduledCell({ row }) {
    var _a;
    const end = (_a = row.nextStop) === null || _a === void 0 ? void 0 : _a.window_end;
    if (!end)
        return React.createElement("span", { className: "text-muted-foreground" }, '—');
    return (React.createElement("span", { className: "text-sm" }, moment(end).tz(getTimezone(row.nextStop)).format('L LT')));
}
export function getCarrierDashboardColumns({ actionType, isCustomerView, onEdit, }) {
    const cols = [
        {
            id: 'status',
            header: () => 'Status',
            cell: ({ row }) => React.createElement(StatusCell, { row: row.original, onClick: onEdit }),
            enableSorting: false,
            size: 180,
        },
        {
            id: 'shipment_id',
            header: () => 'Shipment ID',
            cell: ({ row }) => React.createElement(ShipmentLinkCell, { row: row.original }),
            enableSorting: false,
            size: 160,
        },
        {
            id: 'container',
            header: () => 'Container #',
            cell: ({ row }) => React.createElement(ContainerCell, { row: row.original }),
            enableSorting: false,
            size: 160,
        },
    ];
    if (!isCustomerView) {
        cols.push({
            id: 'shipper',
            header: () => 'Shipper',
            cell: ({ row }) => React.createElement(ShipperCell, { row: row.original }),
            enableSorting: false,
            size: 180,
        });
        cols.push({
            id: 'carrier',
            header: () => 'Carrier',
            cell: ({ row }) => React.createElement(CarrierCell, { row: row.original }),
            enableSorting: false,
            size: 180,
        });
    }
    cols.push({
        id: 'next_action',
        header: () => 'Next Action',
        cell: ({ row }) => React.createElement(NextActionCell, { row: row.original }),
        enableSorting: false,
        size: 200,
    });
    if (actionType === COMPLETE_STOP) {
        cols.push({
            id: 'appt_scheduled',
            header: () => 'Appt Scheduled',
            cell: ({ row }) => React.createElement(ApptScheduledCell, { row: row.original }),
            enableSorting: false,
            size: 160,
        });
    }
    cols.push({
        id: 'edit',
        header: () => '',
        cell: ({ row }) => {
            if (ACTIONS_WITHOUT_EDIT.includes(row.original.action))
                return null;
            return (React.createElement(EditButton, { type: "button", size: "small", onClick: () => onEdit(row.original), title: row.original.action }));
        },
        enableSorting: false,
        size: 60,
    });
    return cols;
}
